/**
 * The time gutter (W1-15 timeline-host; DESIGN.md §4.5-4.6, ARCHITECTURE.md §7.7): the beat ruler at the left edge
 * of the timeline, pinned while the columns scroll sideways.
 *
 * Static layer: beat ticks coloured by division (osu! colours, theme.ts), thinned out by zoom so ticks stay at
 * least MIN_TICK_SPACING_PX apart; bar lines from the editor meter (`Timing.meter`, compiled with core/time/meter
 * so a 3/4 or 7/8 section stays correct); beat numbers (every beat when there is room, otherwise every Nth beat and
 * every bar start).
 * Content layer: decorations from the modules' providers: markers (flag + name), BPM changes (square + label),
 * the loop band, generator bands, selection bands and problem ticks. Problem severities differ by SHAPE as well as
 * colour (fatal square, error filled triangle, warning outlined triangle, tip dot; colour is never the only
 * signal).
 * Overlay layer: the time-range selection, the live loop drag and the playhead marker.
 *
 * Gestures (left button): drag selects a time range (snapped to the beat grid; "Remove selected time range…" and
 * other range tools act on it; the host also selects the notes and keys inside it across every column, DESIGN.md
 * §5.4, with Shift = add, Ctrl/Cmd = toggle), Alt+drag (Ctrl+Shift+drag in the Alt-free variant) sets the loop
 * region, dragging the playhead marker scrubs (RPE NAV-01 "drag the time bar diamond"; snapped, Alt held = no snap,
 * Ctrl+Shift held in the Alt-free variant, DESIGN.md §8.1; Esc returns the playhead), a click seeks to the snapped beat (and clears the time range), a click on a decoration
 * runs its `onClick` (jump to a problem, select a marker). Right-click opens the gutter context menu (the host
 * forwards it; W2-03 contributes "Insert beats at playhead…" / "Remove selected time range…").
 *
 * `drawBeatGrid` is exported for the columns (lane, motion columns) so every column's grid matches the gutter.
 */

import { Beats } from '../../core/time/beat';
import type { MeterMap } from '../../core/time/meter';
import type { Beat } from '../../core/time/types';
import type { TimeAxis, TimelineColumnView, TimelineDecoration, TimelineHit, TimelinePointerEvent } from '../types';
import { FINEST_DIV, forEachTick, snapBeat, visibleSubdivision } from './axis';
import { canvasFont, severityOfDecoration, snapColorClass, type TimelineTheme } from './theme';

export const GUTTER_DEF_ID = 'gutter';
export const GUTTER_INSTANCE_ID = 'gutter';
export const GUTTER_WIDTH = 48;
/** Minimum distance between drawn ticks. */
export const MIN_TICK_SPACING_PX = 6;
/** Minimum distance between beat labels. */
const MIN_LABEL_SPACING_PX = 18;
/** Pointer tolerance for decorations. */
const DECORATION_HIT_PX = 5;
/** Movement before a press becomes a range drag. */
const DRAG_THRESHOLD_PX = 3;
/** Distance from the playhead within which a press grabs it (scrub) instead of starting a range. */
export const PLAYHEAD_GRAB_PX = 6;

/** Modifiers of the press that started a time-range drag (selection mode of the items inside it). */
export interface RangeModifiers {
  readonly shift: boolean;
  /** Ctrl, or Cmd on macOS. */
  readonly ctrl: boolean;
}

export type SeekPhase = 'start' | 'move' | 'end';

/** A time range in exact beats, start < end. */
export interface TimeRange {
  readonly start: Beat;
  readonly end: Beat;
}

export function timeRangeF64(r: TimeRange): readonly [number, number] {
  return [Beats.toF64(r.start), Beats.toF64(r.end)];
}

/** Orders two beats into a range; null when they are equal. */
export function orderRange(a: Beat, b: Beat): TimeRange | null {
  const c = Beats.cmp(a, b);
  if (c === 0) return null;
  return c < 0 ? { start: a, end: b } : { start: b, end: a };
}

export interface BeatGridOptions {
  readonly div: number;
  readonly meter: MeterMap | null;
  readonly theme: TimelineTheme;
  /** Left edge and width of the area to rule (default: the whole column). */
  readonly x?: number;
  readonly width?: number;
  readonly minSpacingPx?: number;
  /** Opacity of sub-beat lines in their division colour (default 0.3). */
  readonly subAlpha?: number;
  /** Opacity of beat lines, drawn 1 px in the grid-beat token (default 1). */
  readonly beatAlpha?: number;
  /** Opacity of bar lines, drawn 2 px in the grid-bar token (default 1). */
  readonly barAlpha?: number;
}

/**
 * Horizontal beat grid across a column, in visual hierarchy order: sub-beat lines in their division colour (faint,
 * osu! colours), beat lines in the grid-beat token, bar lines of the meter in the grid-bar token, 2 px (the tokens
 * of W1-12: --color-grid-sub < --color-grid-beat < --color-grid-bar in prominence). Draws nothing finer than
 * `minSpacingPx`.
 */
export function drawBeatGrid(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  widthPx: number,
  opts: BeatGridOptions,
): void {
  const x = opts.x ?? 0;
  const w = opts.width ?? widthPx;
  if (w <= 0 || axis.heightPx <= 0) return;
  const [b0, b1] = axis.range;
  const sub = visibleSubdivision(opts.div, axis.pxPerBeat, opts.minSpacingPx ?? MIN_TICK_SPACING_PX);
  const theme = opts.theme;
  ctx.save();
  if (sub > 1) {
    ctx.globalAlpha = opts.subAlpha ?? 0.3;
    forEachTick(b0, b1, sub, (beat, _k, den) => {
      if (den === 1) return;
      ctx.fillStyle = theme.snap[snapColorClass(den)];
      ctx.fillRect(x, Math.round(axis.yAtBeat(beat)), w, 1);
    });
    ctx.globalAlpha = 1;
  }
  if (sub >= 1) {
    ctx.globalAlpha = opts.beatAlpha ?? 1;
    ctx.fillStyle = theme.gridBeat;
    forEachTick(b0, b1, 1, (beat) => {
      ctx.fillRect(x, Math.round(axis.yAtBeat(beat)), w, 1);
    });
  }
  if (opts.meter !== null) {
    ctx.globalAlpha = opts.barAlpha ?? 1;
    ctx.fillStyle = theme.gridBar;
    for (const bar of barStarts(opts.meter, b0, b1)) ctx.fillRect(x, Math.round(axis.yAtBeat(bar)) - 1, w, 2);
  }
  ctx.restore();
}

/** Bar starts (f64) inside [b0, b1], at most `limit`, skipping them entirely when bars are sub-pixel. */
export function barStarts(meter: MeterMap, b0: number, b1: number, limit = 4000): number[] {
  if (!(b1 >= b0)) return [];
  const from = snapBeat(b0, FINEST_DIV);
  const to = snapBeat(b1, FINEST_DIV);
  return meter.barStartsBetween(from, to, limit).map((b) => Beats.toF64(b));
}

/** Decoration nearest to y within the hit tolerance (problems first, then markers, BPM, others). */
export function decorationAt(
  decorations: readonly TimelineDecoration[],
  y: number,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  tolPx = DECORATION_HIT_PX,
): TimelineDecoration | null {
  let best: TimelineDecoration | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const d of decorations) {
    if (d.kind === 'selection' || d.kind === 'loop') continue;
    const y0 = axis.yAtBeat(d.beat);
    const y1 = d.endBeat === undefined ? y0 : axis.yAtBeat(d.endBeat);
    const top = Math.min(y0, y1);
    const bottom = Math.max(y0, y1);
    const dist = y < top ? top - y : y > bottom ? y - bottom : 0;
    if (dist > tolPx) continue;
    const rank = severityOfDecoration(d.kind) !== null ? 0 : d.kind === 'marker' ? 1 : d.kind === 'bpm' ? 2 : 3;
    const score = rank * 100 + dist;
    if (score < bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

/** Truncates `text` with an ellipsis to fit `maxPx` (measured with the context's current font). */
export function fitText(ctx: CanvasRenderingContext2D, text: string, maxPx: number): string {
  if (maxPx <= 0) return '';
  if (ctx.measureText(text).width <= maxPx) return text;
  const ell = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + ell).width <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return lo === 0 ? '' : text.slice(0, lo) + ell;
}

/** Draws a problem tick of the given severity centred at (x, y); size in CSS px. */
export function drawSeverityMark(
  ctx: CanvasRenderingContext2D,
  kind: 'fatal' | 'error' | 'warning' | 'tip',
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const r = size / 2;
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  switch (kind) {
    case 'fatal':
      ctx.rect(x - r, y - r, size, size);
      ctx.fill();
      break;
    case 'error':
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      ctx.fill();
      break;
    case 'warning':
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      ctx.stroke();
      break;
    case 'tip':
      ctx.arc(x, y, Math.max(1.5, r * 0.6), 0, Math.PI * 2);
      ctx.fill();
      break;
  }
  ctx.restore();
}

export interface GutterDeps {
  theme(): TimelineTheme;
  meter(): MeterMap;
  snapDiv(): number;
  playhead(): number;
  /** Decorations of every provider for a beat range (the host caches per redraw). */
  decorations(range: readonly [number, number]): readonly TimelineDecoration[];
  /** Committed time range (host state). */
  timeRange(): TimeRange | null;
  /** Snap a display beat to the current grid (or 1/1920 with snap off). */
  snap(beat: number): Beat;
  /** Live / committed / cancelled time-range drags (null = cleared); `mods` are the press modifiers. */
  onRange(range: TimeRange | null, phase: 'live' | 'commit' | 'cancel', mods: RangeModifiers): void;
  /** Alt+drag on the gutter: live / committed / cancelled loop region (null on 'cancel': keep the old loop). */
  onLoop(range: TimeRange | null, phase: 'live' | 'commit' | 'cancel'): void;
  /**
   * Whether a press sets the loop instead of a time range. Default: Alt. The host also accepts Ctrl+Shift in the
   * Alt-free variant (DESIGN.md §8.1: some window managers take Alt+drag).
   */
  isLoopDrag?(e: TimelinePointerEvent): boolean;
  /**
   * Whether the modifiers held during a playhead scrub bypass the snap (1/1920 instead of the grid). Default: Alt.
   * The host also accepts Ctrl+Shift in the Alt-free variant (DESIGN.md §8.1: "Ctrl+Shift held during a drag
   * replaces Alt as the no snap modifier").
   */
  isNoSnap?(e: TimelinePointerEvent): boolean;
  /** A live band drawn by another control (the overview strip's loop drag), shown while this gutter has none. */
  foreignLive?(): { readonly range: TimeRange; readonly loop: boolean } | null;
  /**
   * A click seeks once ('end'); a playhead scrub reports 'start', then 'move' for every new position, then 'end'
   * (Esc: 'end' at the playhead it started from).
   */
  onSeek(beat: number, phase: SeekPhase): void;
  onContextMenu(e: TimelinePointerEvent): void;
  onHover(d: TimelineDecoration | null): void;
  invalidate(layer: 'content' | 'overlay'): void;
}

export interface GutterView extends TimelineColumnView {
  /** Static and content are placed by beats only (TimelineHost `ScrollCacheView`). */
  readonly scrollCache: true;
  onPointerCancel(): void;
  onPointerLeave(): void;
  /** Live drag range (time range or loop), for the host's cross-column band. */
  readonly liveRange: { readonly range: TimeRange; readonly loop: boolean } | null;
}

const LABEL_FONT_PX = 10;

export function createGutterView(deps: GutterDeps): GutterView {
  let width = GUTTER_WIDTH;
  let press: {
    readonly mode: 'range' | 'loop' | 'scrub';
    readonly startY: number;
    readonly startBeat: number;
    readonly mods: RangeModifiers;
    readonly decoration: TimelineDecoration | null;
    /** Playhead at the press (a cancelled scrub returns there). */
    readonly playheadAtPress: number;
    /** Last beat a scrub reported. */
    scrubBeat: number | null;
    moved: boolean;
  } | null = null;
  let live: { range: TimeRange; loop: boolean } | null = null;
  let hovered: TimelineDecoration | null = null;
  let lastDecorations: readonly TimelineDecoration[] = [];
  let lastAxis: TimeAxis | null = null;

  const decorationsFor = (axis: TimeAxis): readonly TimelineDecoration[] => {
    lastDecorations = deps.decorations(axis.range);
    lastAxis = axis;
    return lastDecorations;
  };

  const drawStatic = (ctx: CanvasRenderingContext2D, axis: TimeAxis): void => {
    const theme = deps.theme();
    ctx.fillStyle = theme.gutterBackground;
    ctx.fillRect(0, 0, width, axis.heightPx);
    const [b0, b1] = axis.range;
    const div = deps.snapDiv();
    const sub = visibleSubdivision(div, axis.pxPerBeat, MIN_TICK_SPACING_PX);
    // Division ticks at the right edge, longest on the beat.
    if (sub >= 1) {
      forEachTick(b0, b1, sub, (beat, _k, den) => {
        const len = den === 1 ? 14 : den === 2 ? 10 : 6;
        ctx.fillStyle = theme.snap[snapColorClass(den)];
        ctx.fillRect(width - len, Math.round(axis.yAtBeat(beat)), len, 1);
      });
    }
    // Bar lines across the gutter (grid-bar token, as in the columns' drawBeatGrid).
    const bars = barStarts(deps.meter(), b0 - 1, b1 + 1);
    ctx.fillStyle = theme.gridBar;
    for (const bar of bars) ctx.fillRect(0, Math.round(axis.yAtBeat(bar)) - 1, width, 2);
    // Beat numbers: every `every` beats, plus bar starts when they have room.
    const every = labelStride(axis.pxPerBeat);
    const barSet = new Set(bars);
    const barRoom = minGap(bars) * axis.pxPerBeat >= MIN_LABEL_SPACING_PX;
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left';
    const first = Math.ceil(b0 - 1);
    const last = Math.floor(b1 + 1);
    for (let beat = first; beat <= last; beat++) {
      const isBar = barSet.has(beat);
      if (beat % every !== 0 && !(isBar && barRoom)) continue;
      const y = Math.round(axis.yAtBeat(beat));
      ctx.font = canvasFont(theme, LABEL_FONT_PX, 'mono', isBar ? 600 : 400);
      ctx.fillStyle = isBar ? theme.text : theme.textMuted;
      ctx.fillText(String(beat), 4, y - 1);
    }
  };

  const drawContent = (ctx: CanvasRenderingContext2D, axis: TimeAxis): void => {
    const theme = deps.theme();
    const decs = decorationsFor(axis);
    // Bands first (loop, selection, generator), then point decorations on top.
    for (const d of decs) {
      if (d.endBeat === undefined) continue;
      const yA = axis.yAtBeat(d.beat);
      const yB = axis.yAtBeat(d.endBeat);
      const top = Math.min(yA, yB);
      const h = Math.max(1, Math.abs(yB - yA));
      if (d.kind === 'loop') {
        ctx.fillStyle = theme.loopFill;
        ctx.fillRect(0, top, width, h);
        ctx.fillStyle = theme.loop;
        ctx.fillRect(0, top, 3, h);
        ctx.fillRect(0, Math.round(yA) - 1, width, 2);
        ctx.fillRect(0, Math.round(yB) - 1, width, 2);
      } else if (d.kind === 'selection') {
        ctx.fillStyle = theme.selection;
        ctx.fillRect(0, top, width, h);
      } else if (d.kind === 'generator') {
        ctx.fillStyle = theme.generator;
        ctx.fillRect(width - 3, top, 3, h);
      }
    }
    for (const d of decs) {
      const y = Math.round(axis.yAtBeat(d.beat));
      const sev = severityOfDecoration(d.kind);
      if (sev !== null) {
        const color = theme.severity[sev];
        if (d.endBeat !== undefined) {
          const y1 = axis.yAtBeat(d.endBeat);
          ctx.fillStyle = color;
          ctx.fillRect(width - 2, Math.min(y, y1), 2, Math.max(1, Math.abs(y1 - y)));
        }
        drawSeverityMark(ctx, sev, width - 7, y, 7, color);
      } else if (d.kind === 'marker') {
        ctx.fillStyle = theme.marker;
        ctx.fillRect(0, y, width, 1);
        ctx.beginPath();
        ctx.moveTo(0, y + 1);
        ctx.lineTo(6, y + 4);
        ctx.lineTo(0, y + 7);
        ctx.closePath();
        ctx.fill();
        if (d.label !== undefined && d.label !== '') {
          // Below the line: beat numbers sit above their lines.
          ctx.font = canvasFont(theme, 9, 'sans', 600);
          ctx.textBaseline = 'top';
          ctx.textAlign = 'left';
          ctx.fillText(fitText(ctx, d.label, width - 10), 8, y + 2);
        }
      } else if (d.kind === 'bpm') {
        ctx.fillStyle = theme.bpm;
        ctx.fillRect(width - 16, y, 16, 1);
        ctx.fillRect(width - 4, y - 2, 4, 4);
        if (d.label !== undefined && d.label !== '') {
          ctx.font = canvasFont(theme, 9, 'mono', 600);
          ctx.textBaseline = 'top';
          ctx.textAlign = 'right';
          ctx.fillText(fitText(ctx, d.label, width - 6), width - 5, y + 2);
        }
      }
    }
    if (hovered !== null) {
      const y = Math.round(axis.yAtBeat(hovered.beat));
      ctx.strokeStyle = theme.accent;
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, y - 6.5, width - 1, 12);
    }
  };

  const drawOverlay = (ctx: CanvasRenderingContext2D, axis: TimeAxis): void => {
    const theme = deps.theme();
    const shown =
      live ??
      deps.foreignLive?.() ??
      (() => {
        const r = deps.timeRange();
        return r === null ? null : { range: r, loop: false };
      })();
    if (shown !== null) {
      const [s, e] = timeRangeF64(shown.range);
      const yS = axis.yAtBeat(s);
      const yE = axis.yAtBeat(e);
      ctx.fillStyle = shown.loop ? theme.loopFill : theme.selection;
      ctx.fillRect(0, yE, width, yS - yE);
      ctx.fillStyle = shown.loop ? theme.loop : theme.accent;
      ctx.fillRect(0, Math.round(yS) - 1, width, 2);
      ctx.fillRect(0, Math.round(yE) - 1, width, 2);
      ctx.fillRect(width - 3, yE, 3, yS - yE);
    }
    // Playhead marker: a triangle at the right edge (the host draws the line across every column).
    const yP = axis.yAtBeat(deps.playhead());
    if (yP >= -6 && yP <= axis.heightPx + 6) {
      ctx.fillStyle = theme.playhead;
      ctx.beginPath();
      ctx.moveTo(width, yP - 5);
      ctx.lineTo(width - 7, yP);
      ctx.lineTo(width, yP + 5);
      ctx.closePath();
      ctx.fill();
    }
  };

  const setHover = (d: TimelineDecoration | null): void => {
    if (d === hovered) return;
    hovered = d;
    deps.onHover(d);
    deps.invalidate('content');
  };

  const nearPlayhead = (y: number, axis: TimeAxis): boolean =>
    Math.abs(axis.yAtBeat(deps.playhead()) - y) <= PLAYHEAD_GRAB_PX;

  /**
   * Scrub target: the grid (or 1/1920 with snap off), or 1/1920 while the no-snap modifier is held (Alt; Ctrl+Shift
   * in the Alt-free variant; DESIGN.md §8.1-8.2).
   */
  const scrubTarget = (e: TimelinePointerEvent): number =>
    Beats.toF64((deps.isNoSnap?.(e) ?? e.alt) ? snapBeat(e.beat, FINEST_DIV) : deps.snap(e.beat));

  const view: GutterView = {
    scrollCache: true,
    get liveRange() {
      return live;
    },
    layout(w) {
      width = w;
    },
    draw(layer, ctx, axis, widthPx) {
      width = widthPx;
      if (layer === 'static') drawStatic(ctx, axis);
      else if (layer === 'content') drawContent(ctx, axis);
      else drawOverlay(ctx, axis);
    },
    hitTest(_x, y, axis) {
      const decs = lastAxis === axis ? lastDecorations : decorationsFor(axis);
      const d = decorationAt(decs, y, axis);
      const cursor = nearPlayhead(y, axis) ? 'grab' : d !== null && d.onClick !== undefined ? 'pointer' : 'row-resize';
      return { kind: 'gutter', cursor } satisfies TimelineHit;
    },
    onPointerDown(e) {
      if ((e.buttons & 1) === 0) return;
      const decs = lastDecorations;
      const d = lastAxis === null ? null : decorationAt(decs, e.y, lastAxis);
      const loop = deps.isLoopDrag?.(e) ?? e.alt;
      const scrub = !loop && lastAxis !== null && nearPlayhead(e.y, lastAxis);
      press = {
        mode: loop ? 'loop' : scrub ? 'scrub' : 'range',
        startY: e.y,
        startBeat: e.beat,
        mods: { shift: e.shift, ctrl: e.ctrl },
        decoration: d,
        playheadAtPress: deps.playhead(),
        scrubBeat: null,
        moved: false,
      };
    },
    onPointerMove(e) {
      if (press === null) {
        if (lastAxis !== null) setHover(decorationAt(lastDecorations, e.y, lastAxis));
        return;
      }
      if (!press.moved && Math.abs(e.y - press.startY) < DRAG_THRESHOLD_PX) return;
      const first = !press.moved;
      press.moved = true;
      if (press.mode === 'scrub') {
        const beat = scrubTarget(e);
        if (!first && beat === press.scrubBeat) return;
        press.scrubBeat = beat;
        deps.onSeek(beat, first ? 'start' : 'move');
        return;
      }
      const range = orderRange(deps.snap(press.startBeat), deps.snap(e.beat));
      if (range === null) {
        if (live !== null) {
          live = null;
          deps.invalidate('overlay');
        }
        return;
      }
      const loop = press.mode === 'loop';
      live = { range, loop };
      if (loop) deps.onLoop(range, 'live');
      else deps.onRange(range, 'live', press.mods);
      deps.invalidate('overlay');
    },
    onPointerUp(e) {
      const p = press;
      press = null;
      if (p === null) return;
      const range = live?.range ?? null;
      live = null;
      if (!p.moved) {
        if (p.decoration?.onClick !== undefined) p.decoration.onClick();
        else if (p.mode !== 'loop') {
          deps.onRange(null, 'commit', p.mods);
          deps.onSeek(Beats.toF64(deps.snap(e.beat)), 'end');
        }
      } else if (p.mode === 'scrub') {
        deps.onSeek(scrubTarget(e), 'end');
      } else if (p.mode === 'loop') {
        deps.onLoop(range, range === null ? 'cancel' : 'commit');
      } else {
        deps.onRange(range, 'commit', p.mods);
      }
      deps.invalidate('overlay');
    },
    onPointerCancel() {
      const p = press;
      press = null;
      if (p === null) return;
      if (p.mode === 'scrub') {
        if (p.moved) deps.onSeek(p.playheadAtPress, 'end');
      } else if (live !== null) {
        if (p.mode === 'loop') deps.onLoop(null, 'cancel');
        else deps.onRange(null, 'cancel', p.mods);
      }
      live = null;
      deps.invalidate('overlay');
    },
    onPointerLeave() {
      if (press === null) setHover(null);
    },
    onContextMenu(e) {
      deps.onContextMenu(e);
    },
    dispose() {
      press = null;
      live = null;
      hovered = null;
      lastDecorations = [];
      lastAxis = null;
    },
  };
  return view;
}

/** Label every Nth beat so labels stay MIN_LABEL_SPACING_PX apart (1, 2, 4, 8, ...). */
export function labelStride(pxPerBeat: number): number {
  let n = 1;
  while (n * pxPerBeat < MIN_LABEL_SPACING_PX && n < 1 << 20) n *= 2;
  return n;
}

function minGap(sorted: readonly number[]): number {
  let g = Number.POSITIVE_INFINITY;
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1];
    const b = sorted[i];
    if (a !== undefined && b !== undefined && b - a < g) g = b - a;
  }
  return g;
}
